import { Link } from "react-router-dom";
import LocalLink from "@/components/ui/LocalLink";
import { AnimatedText, AnimatedTitle } from "@/components/ui/AnimatedText";
import { ChevronDown } from "lucide-react";
import BookingWidget from "@/components/ui/BookingWidget";
import { useSEO } from "@/utils/useSEO";
import { useTranslation, Trans } from "react-i18next";

// Swiper slider imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function Home() {
  const { t, i18n } = useTranslation();
  
  const getPath = (path: string) => {
    if (i18n.language === 'en' || !i18n.language) return path;
    return `/${i18n.language}${path}`;
  };
  useSEO(
    "Home",
    "Awaken to Bali's most breathtaking panorama at Patal Kikian Villas, Sidemen. Embrace the majestic presence of Mount Agung as it watches over emerald rice terraces and lush rolling valleys. Here in the peaceful embrace of Sidemen, crisp mountain air and untouched serenity weave together to create an unforgettable sanctuary for your soul.",
    "/media/patal-kikian-hero-banner.webp"
  );


  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-[100dvh] w-full flex flex-col items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="/media/patal-kikian-hero-banner.webp" alt="Patal Kikian Background" fetchPriority="high" className="w-full h-full object-cover opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 md:px-6 text-left text-white flex flex-col items-start justify-end flex-1 w-full pb-20 md:pb-32 pt-24 md:pt-32">
          <AnimatedText delay={0.2} className="text-gold uppercase tracking-[0.2em] md:tracking-[0.3em] text-[10px] md:text-xs mb-2 md:mb-3 font-semibold">
            {t('home.hero.subtitle')}
          </AnimatedText>
          <AnimatedTitle
            title={t("home.hero.title")}
            className="text-3xl md:text-5xl lg:text-6xl mb-3 md:mb-4 max-w-4xl leading-tight font-luxury text-shadow-lg"
          />
          <AnimatedText delay={0.4}>
            <p className="text-xs md:text-base text-white/90 max-w-2xl font-light leading-relaxed mb-6 md:mb-8 text-shadow">
              {t('home.hero.description')}
            </p>
          </AnimatedText>
          
        </div>
      
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <button 
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center gap-2 cursor-pointer"
            aria-label="Scroll to content"
          >
            
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </section>

      {/* Inspirational Intro Section */}
      <section className="py-20 md:py-32 bg-background relative overflow-hidden">
        <div className="container mx-auto px-6 max-w-4xl text-center relative z-10">
          <AnimatedText delay={0.1}>
            <h2 className="text-2xl md:text-4xl font-luxury text-gold mb-8 md:mb-12 leading-relaxed">
              {t('home.intro.quote')}
            </h2>
          </AnimatedText>
          <div className="space-y-6 md:space-y-8 text-white/80 font-light leading-relaxed md:text-lg">
            <AnimatedText delay={0.2}>
              <p>
                <Trans i18nKey="home.intro.p1" components={{ span: <span className="italic text-white" /> }} />
              </p>
            </AnimatedText>
            <AnimatedText delay={0.3}>
              <p>
                {t('home.intro.p2')}
              </p>
            </AnimatedText>
            <AnimatedText delay={0.4}>
              <p>
                {t('home.intro.p3')}
              </p>
            </AnimatedText>
            <AnimatedText delay={0.5}>
              <p>
                {t('home.intro.p4')}
              </p>
            </AnimatedText>
            <AnimatedText delay={0.6}>
              <p>
                {t('home.intro.p5')}
              </p>
            </AnimatedText>
            <AnimatedText delay={0.7} className="pt-8 md:pt-12 border-t border-white/10 mt-8 md:mt-12">
              <p className="text-xl md:text-2xl text-white font-luxury italic mb-4">
                {t('home.intro.footerQuote')}
              </p>
              <p className="text-gold uppercase tracking-[0.2em] text-sm md:text-base font-semibold mt-6">
                Patal Kikian Villas
              </p>
              <p className="text-white/60 text-xs md:text-sm tracking-[0.3em] mt-2 uppercase">
                {t('home.intro.footerTagline')}
              </p>
            </AnimatedText>
          </div>
        </div>
      </section>

      {/* Restaurant Slider Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6 text-center">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-widest text-xs font-semibold">{t("home.restaurant.subtitle")}</span>
          </AnimatedText>
          <AnimatedTitle title={t("home.restaurant.title")} className="text-3xl md:text-5xl mt-3 mb-6 font-luxury" />
          <AnimatedText delay={0.2}>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed mb-12">
              {t('home.restaurant.desc')}
            </p>
          </AnimatedText>
          
          <AnimatedText delay={0.2}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              navigation={true}
              pagination={{ clickable: true }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              loop={true}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="w-full !pb-12"
            >
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/patal-kikian-restaurant%20(1).webp" alt="Restaurant View 1" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/patal-kikian-restaurant%20(2).webp" alt="Restaurant View 2" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/patal-restaurant-1.webp" alt="Restaurant" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/patal-restaurant-outdoor.webp" alt="Outdoor Restaurant" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/romantic-dinner-patal.webp" alt="Romantic Dinner" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/patal-kikian-hero-banner.webp" alt="Patal Kikian Hero" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
            </Swiper>
          </AnimatedText>

          <AnimatedText delay={0.3}>
            <div className="mt-8 flex flex-col items-center">
              <p className="text-white/70 max-w-xl mx-auto leading-relaxed mb-6">
                {t('home.restaurant.callToAction')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <LocalLink to={getPath("/restaurant-menu")} className="px-8 py-3 bg-gold text-background font-medium rounded-sm hover:bg-white transition-colors duration-300">
                  {t('home.restaurant.btnMenu')}
                </LocalLink>
                <a href={`https://wa.me/6281353278504?text=${encodeURIComponent('Hello, I would like to make a reservation for Patal Kikian Restaurant.')}`} target="_blank" rel="noopener noreferrer" className="px-8 py-3 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300">
                  {t('home.restaurant.btnReserve')}
                </a>
                <a href={`https://wa.me/6281353278504?text=${encodeURIComponent('Hello, I would like to book a Special Transport.')}`} target="_blank" rel="noopener noreferrer" className="px-6 py-2 flex flex-col items-center justify-center bg-gold/10 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 text-center">
                  <span className="leading-tight">{t('home.restaurant.btnTransport')}</span>
                  <span className="text-[10px] font-normal tracking-widest leading-tight mt-0.5">{t('home.restaurant.btnTransportSub')}</span>
                </a>
                <a href="https://www.google.com/maps?q=Patal+Kikian+Villas+Sidemen" target="_blank" rel="noopener noreferrer" className="px-6 py-2 flex flex-col items-center justify-center bg-gold/10 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 text-center">
                  <span className="leading-tight">{t('home.restaurant.btnMaps')}</span>
                  <span className="text-[10px] font-normal tracking-widest leading-tight mt-0.5">{t('home.restaurant.btnMapsSub')}</span>
                </a>
              </div>
            </div>
          </AnimatedText>
        </div>
      </section>

      {/* Spa Slider Section */}
      <section className="py-24 bg-background border-t border-white/5">
        <div className="container mx-auto px-6 text-center">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-widest text-xs font-semibold">{t("home.different.subtitle")}</span>
          </AnimatedText>
          <AnimatedTitle title={t("home.spa.title")} className="text-3xl md:text-5xl mt-3 mb-6 font-luxury" />
          <AnimatedText delay={0.2}>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed mb-12">
              {t('home.spa.desc')}
            </p>
          </AnimatedText>
          
          <AnimatedText delay={0.2}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              navigation={true}
              pagination={{ clickable: true }}
              autoplay={{ delay: 6000, disableOnInteraction: false }}
              loop={true}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="w-full !pb-12"
            >
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/spa-patal-sunrise.webp" alt="Spa Sunrise" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/spa-patal.webp" alt="Patal Spa" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/patal-spa.webp" alt="Spa" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/spa%20(1).webp" alt="Relaxation" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/spa%20(2).webp" alt="Massage" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/spa%20(3).webp" alt="Spa Treatment" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/patal-spa-view.webp" alt="Spa View" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
            </Swiper>
          </AnimatedText>

          <AnimatedText delay={0.3}>
            <div className="mt-8 flex flex-col items-center">
              <p className="text-white/70 max-w-xl mx-auto leading-relaxed mb-6">
                {t('home.spa.callToAction')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <LocalLink to={getPath("/spa-menu")} className="px-8 py-3 bg-gold text-background font-medium rounded-sm hover:bg-white transition-colors duration-300">
                  View Menu
                </LocalLink>
                <a href={`https://wa.me/6281353278504?text=${encodeURIComponent('Hello, I would like to make a reservation for Patal Kikian Spa.')}`} target="_blank" rel="noopener noreferrer" className="px-8 py-3 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300">
                  Reservation
                </a>
                <a href={`https://wa.me/6281353278504?text=${encodeURIComponent('Hello, I would like to book a Special Transport.')}`} target="_blank" rel="noopener noreferrer" className="px-6 py-2 flex flex-col items-center justify-center bg-gold/10 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 text-center">
                  <span className="leading-tight">Special Transport Reservation</span>
                  <span className="text-[10px] font-normal tracking-widest leading-tight mt-0.5">(Sidemen Area)</span>
                </a>
                <a href="https://www.google.com/maps?q=Patal+Kikian+Villas+Sidemen" target="_blank" rel="noopener noreferrer" className="px-6 py-2 flex flex-col items-center justify-center bg-gold/10 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 text-center">
                  <span className="leading-tight">Google Maps</span>
                  <span className="text-[10px] font-normal tracking-widest leading-tight mt-0.5">Location</span>
                </a>
              </div>
            </div>
          </AnimatedText>
        </div>
      </section>

      {/* Rooms Slider Section */}
      <section className="py-24 bg-background border-t border-white/5">
        <div className="container mx-auto px-6 text-center">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-widest text-xs font-semibold">Patal Kikian Sidemen</span>
          </AnimatedText>
          <AnimatedTitle title={t("home.rooms.title")} className="text-3xl md:text-5xl mt-3 mb-6 font-luxury" />
          <AnimatedText delay={0.2}>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed mb-12">
              {t('home.rooms.desc')}
            </p>
          </AnimatedText>
          <AnimatedText delay={0.2}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              navigation={true}
              pagination={{ clickable: true }}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              loop={true}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="w-full !pb-12"
            >
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/private-pool-villa/private-pool-villa-night-scene-1.webp" alt="Private Pool Villa Night Scene 1" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/private-pool-villa/private-pool-villa-night-scene-2.webp" alt="Private Pool Villa Night Scene 2" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/private-pool-villa/private-pool-villa-night-scene-3.webp" alt="Private Pool Villa Night Scene 3" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/suite-room/suite-room (6).webp" alt="Suite Room View" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/deluxe-bungalow/deluxe-bungalow (5).webp" alt="Deluxe Bungalow View" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/private-pool-villa/private-pool-villa (1).webp" alt="Private Pool Villa Day View" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/suite-room/suite-room (2).webp" alt="Suite Room View 2" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/rooms/deluxe-bungalow/deluxe-bungalow (1).webp" alt="Deluxe Bungalow View 2" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
            </Swiper>
          </AnimatedText>

          <AnimatedText delay={0.3}>
            <div className="mt-8 flex justify-center">
              <a href="https://www.google.com/maps?q=Patal+Kikian+Villas+Sidemen" target="_blank" rel="noopener noreferrer" className="px-8 py-3 flex items-center justify-center border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300">
                Google Maps
              </a>
            </div>
          </AnimatedText>
        </div>
      </section>

      {/* Culture Experiences Section */}
      <section className="py-24 bg-background border-t border-white/5">
        <div className="container mx-auto px-6 text-center">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-widest text-xs font-semibold">{t("home.culture.subtitle")}</span>
          </AnimatedText>
          <AnimatedTitle title={t("home.culture.title")} className="text-3xl md:text-5xl mt-3 mb-6 font-luxury" />
          <AnimatedText delay={0.2}>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed mb-12">
              {t('home.culture.desc')}
            </p>
          </AnimatedText>

          <AnimatedText delay={0.3}>
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col md:flex-row gap-16 items-center text-left">
                <div className="w-full md:w-1/2 aspect-[4/3] rounded-2xl overflow-hidden relative shadow-2xl">
                  <img src="/media/melukat-sidemen.webp" alt="Sidemen Purification" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <AnimatedText delay={0.4}>
                    <span className="text-gold uppercase tracking-widest text-sm font-semibold">{t("home.culture.journeySub")}</span>
                  </AnimatedText>
                  <AnimatedTitle title={t("home.culture.journeyTitle")} className="text-4xl md:text-5xl mt-4 mb-6 font-luxury" />
                  <AnimatedText delay={0.5}>
                    <p className="text-white/70 leading-relaxed mb-8 text-lg">
                      {t('home.culture.journeyDesc')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 mt-8">
                      <a href="https://sidemenpurification.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 px-8 py-4 uppercase tracking-widest text-sm">
                        {t('home.culture.btnReserve')}
                      </a>
                      <a href={`https://wa.me/6281353278504?text=${encodeURIComponent('Hello, I would like to book a Special Transport.')}`} target="_blank" rel="noopener noreferrer" className="px-6 py-2 flex flex-col items-center justify-center bg-gold/10 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 text-center">
                        <span className="leading-tight">Special Transport Reservation</span>
                        <span className="text-[10px] font-normal tracking-widest leading-tight mt-0.5">(Sidemen Area)</span>
                      </a>
                    </div>
                  </AnimatedText>
                </div>
              </div>
            </div>
          </AnimatedText>
        </div>
      </section>


      {/* Activities Section */}
      <section className="py-24 bg-background border-t border-white/5">
        <div className="container mx-auto px-6 text-center">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-widest text-xs font-semibold">{t("home.activities.subtitle")}</span>
          </AnimatedText>
          <AnimatedTitle title={t("home.activities.title")} className="text-3xl md:text-5xl mt-3 mb-6 font-luxury" />
          <AnimatedText delay={0.2}>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed mb-12">
              {t('home.activities.desc')}
            </p>
          </AnimatedText>
            
            <AnimatedText delay={0.3}>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                navigation={true}
                pagination={{ clickable: true }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                loop={true}
                breakpoints={{
                  768: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                className="w-full !pb-12"
              >
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/mount-agung-trekking.webp" alt="Mount Agung Trekking" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/waterfall-tour.webp" alt="Waterfall Tour" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/atv-adventure.webp" alt="ATV Adventure" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/batur-jeep-tour.webp" alt="Batur Jeep Tour" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/cooking-class.webp" alt="Cooking Class" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/yoga-wellness.webp" alt="Yoga & Wellness" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/rafting.webp" alt="Rafting" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img src="/media/activities/swing.webp" alt="Swing" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </SwiperSlide>
              </Swiper>
            </AnimatedText>
            
            <AnimatedText delay={0.4} className="mt-8">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <LocalLink to={getPath("/experiences")} className="px-8 py-3 bg-gold text-background font-medium rounded-sm hover:bg-white transition-colors duration-300">
                  {t('home.activities.btnView')}
                </LocalLink>
                <LocalLink to={getPath("/experiences")} className="px-8 py-3 flex items-center justify-center border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300">
                  {t('home.activities.btnReserve')}
                </LocalLink>
                <a href={`https://wa.me/6281353278504?text=${encodeURIComponent('Hello, I would like to book a Special Transport.')}`} target="_blank" rel="noopener noreferrer" className="px-6 py-2 flex flex-col items-center justify-center bg-gold/10 border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 text-center">
                  <span className="leading-tight">Special Transport Reservation</span>
                  <span className="text-[10px] font-normal tracking-widest leading-tight mt-0.5">(Sidemen Area)</span>
                </a>
              </div>
            </AnimatedText>
          </div>
      </section>


      {/* What Makes Us Different Section */}
      <section className="py-24 bg-background border-t border-white/5">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <AnimatedText delay={0.1}>
              <span className="text-gold uppercase tracking-widest text-xs font-semibold">Patal Kikian Sidemen</span>
            </AnimatedText>
            <AnimatedTitle title={t("home.different.title")} className="text-3xl md:text-5xl mt-3 mb-6 font-luxury" />
            <AnimatedText delay={0.2}>
              <p className="text-white/70 leading-relaxed text-sm md:text-base">
                {t('home.different.desc')}
              </p>
            </AnimatedText>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <AnimatedText delay={0.2} className="bg-secondary/30 border border-white/5 rounded-2xl p-8 text-center flex flex-col items-center hover:border-gold/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-gold" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
              <span className="text-white/40 uppercase tracking-widest text-[10px] font-semibold mb-2 block">
                {t('home.different.card1.sub')}
              </span>
              <h3 className="text-xl font-luxury text-gold mb-4 uppercase tracking-wider">
                {t('home.different.card1.title')}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                {t('home.different.card1.desc')}
              </p>
            </AnimatedText>

            {/* Card 2 */}
            <AnimatedText delay={0.3} className="bg-secondary/30 border border-white/5 rounded-2xl p-8 text-center flex flex-col items-center hover:border-gold/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-gold" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
              <span className="text-white/40 uppercase tracking-widest text-[10px] font-semibold mb-2 block">
                {t('home.different.card2.sub')}
              </span>
              <h3 className="text-xl font-luxury text-gold mb-4 uppercase tracking-wider">
                {t('home.different.card2.title')}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                {t('home.different.card2.desc')}
              </p>
            </AnimatedText>

            {/* Card 3 */}
            <AnimatedText delay={0.4} className="bg-secondary/30 border border-white/5 rounded-2xl p-8 text-center flex flex-col items-center hover:border-gold/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-gold" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
              <span className="text-white/40 uppercase tracking-widest text-[10px] font-semibold mb-2 block">
                {t('home.different.card3.sub')}
              </span>
              <h3 className="text-xl font-luxury text-gold mb-4 uppercase tracking-wider">
                {t('home.different.card3.title')}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                {t('home.different.card3.desc')}
              </p>
            </AnimatedText>
          </div>
        </div>
      </section>


      {/* Booking Section */}
      <section className="py-24 bg-background border-t border-white/5 relative z-30">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <AnimatedTitle title={t("home.booking.title")} className="text-3xl md:text-4xl mb-4 font-luxury" />
            <AnimatedText delay={0.2}>
              <p className="text-white/70">{t('home.booking.desc')}</p>
            </AnimatedText>
          </div>
          <BookingWidget />
        </div>
      </section>

      {/* Reach Us Section */}
      <section className="py-32 border-t border-white/5 relative overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/media/rooms/private-pool-villa/private-pool-villa (1).webp"
            alt="Reach Us Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/80 z-10" />
        </div>
        
        <div className="relative z-20 container mx-auto px-6 text-center">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">{t("home.contact.subtitle")}</span>
          </AnimatedText>
          <AnimatedTitle title={t("home.contact.title")} className="text-4xl md:text-5xl mt-4 mb-8 font-luxury" />
          <AnimatedText delay={0.3}>
            <p className="text-white/70 leading-relaxed max-w-2xl mx-auto mb-12">
              {t('home.contact.desc')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href="mailto:info@patalkikianbali.com"
                className="px-8 py-4 bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold transition-all duration-300 uppercase tracking-widest text-sm font-semibold w-full sm:w-auto"
              >
                {t('home.contact.btnEmail')}
              </a>
              <a 
                href="https://wa.me/6281353278504"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gold border border-gold text-black hover:bg-white hover:border-white transition-all duration-300 uppercase tracking-widest text-sm font-semibold w-full sm:w-auto"
              >
                {t('home.contact.btnWa')}
              </a>
            </div>
          </AnimatedText>
        </div>
      </section>
    </>
  );
}
